import { Link } from 'react-router-dom';
import { IconChart, IconLink, IconQr, IconShield } from '../components/Icons';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950">
      <header className="absolute inset-x-0 top-0 z-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-2 font-bold tracking-tight">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white">
              S
            </span>
            SLS Short
          </Link>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="btn-ghost text-sm">
              Sign in
            </Link>
            <Link to="/login?signup=1" className="btn-primary text-sm">
              Get started
            </Link>
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden pt-32 pb-20">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-50 via-white to-violet-50 dark:from-brand-950/40 dark:via-ink-950 dark:to-violet-950/30" />
        <div className="absolute -z-10 inset-0 [background-image:radial-gradient(circle_at_top_right,rgba(124,58,237,0.10),transparent_50%)]" />
        <div className="mx-auto max-w-6xl px-6 text-center">
          <span className="inline-block rounded-full border border-brand-200 dark:border-brand-800 bg-white/70 dark:bg-ink-900/70 px-3 py-1 text-xs font-medium text-brand-700 dark:text-brand-300">
            Modern URL shortening, built for teams
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-ink-900 dark:text-ink-50">
            Enterprise URL shortening,
            <br />
            <span className="bg-gradient-to-r from-brand-600 to-violet-600 bg-clip-text text-transparent">
              elegantly engineered.
            </span>
          </h1>
          <p className="mt-6 text-lg text-ink-600 dark:text-ink-300 max-w-2xl mx-auto">
            Branded short links, dynamic QR codes, password-protected destinations, expiration
            controls, click campaigns, and detailed first-party analytics — served from a global
            edge network.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/login?signup=1" className="btn-primary px-5 py-3">
              Create your workspace
            </Link>
            <Link
              to="/docs"
              className="btn-secondary px-5 py-3"
            >
              View documentation
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-20 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Feature
          icon={<IconLink />}
          title="Branded short links"
          description="Custom aliases, tags, UTM builder, expiration dates, and per-link click ceilings."
        />
        <Feature
          icon={<IconQr />}
          title="Dynamic QR codes"
          description="Brandable QR codes with adjustable colors and error correction. Download as SVG."
        />
        <Feature
          icon={<IconChart />}
          title="First-party analytics"
          description="Geographic and device breakdowns, referrers, timeline charts. Export to CSV."
        />
        <Feature
          icon={<IconShield />}
          title="Built-in security"
          description="Password protection, rate limiting, audit logs, and single sign-on for the admin console."
        />
      </section>

      <section className="bg-white dark:bg-ink-900 border-y border-ink-200 dark:border-ink-800">
        <div className="mx-auto max-w-6xl px-6 py-16 grid gap-8 md:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-ink-900 dark:text-ink-50">
              Built for teams that take links seriously.
            </h2>
            <p className="mt-4 text-ink-600 dark:text-ink-300">
              Multi-tenant from day one. Each organization gets isolated link inventory, scoped
              roles, audit logs, and exportable reporting. The admin backend is protected by
              single sign-on through your existing identity provider.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-ink-700 dark:text-ink-200">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-500" /> Role-based access
                control with six built-in roles
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-500" /> Privacy-preserving
                visitor hashing with redacted IP display
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-500" /> Soft-delete and
                restore for every link
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-500" /> CSV import / export
                and webhook framework
              </li>
            </ul>
          </div>
          <div className="card p-6 bg-gradient-to-br from-brand-50 to-violet-50 dark:from-brand-900/30 dark:to-violet-900/30">
            <div className="font-mono text-xs text-ink-600 dark:text-ink-300 mb-2">$ curl https://sls-short.com/abc123</div>
            <div className="font-mono text-xs text-ink-500">→ 302 Found</div>
            <div className="font-mono text-xs text-ink-500">→ Location: https://your-destination.example.com</div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-white/60 dark:bg-ink-900/60 p-3">
                <div className="text-2xl font-bold text-brand-700 dark:text-brand-300">12.4k</div>
                <div className="text-xs text-ink-500 dark:text-ink-400">Clicks today</div>
              </div>
              <div className="rounded-lg bg-white/60 dark:bg-ink-900/60 p-3">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">98.7%</div>
                <div className="text-xs text-ink-500 dark:text-ink-400">Uptime</div>
              </div>
              <div className="rounded-lg bg-white/60 dark:bg-ink-900/60 p-3">
                <div className="text-2xl font-bold text-violet-600 dark:text-violet-300">42ms</div>
                <div className="text-xs text-ink-500 dark:text-ink-400">P50 redirect</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-ink-200 dark:border-ink-800 py-10 text-center text-xs text-ink-500 dark:text-ink-400">
        © {new Date().getFullYear()} SLS Short.
      </footer>
    </div>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: JSX.Element;
  title: string;
  description: string;
}) {
  return (
    <div className="card p-5">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
        {icon}
      </div>
      <h3 className="mt-3 font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
      <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">{description}</p>
    </div>
  );
}
